import React from 'react';
import { BaseTool, IToolManifest, IToolContext } from '../interfaces/ITool';
import { BaseCommand, ICommand } from '../../commands/ICommand';
import { ToolComponentFactory } from '../components/ToolComponents';
import { Plus, Square, Circle, Triangle } from 'lucide-react';

// Generic command for creating shapes
export class CreateShapeCommand extends BaseCommand {
  protected shape: any;
  protected addShape: (shape: any) => void;
  protected deleteShape: (id: string) => void;
  
  constructor(
    shape: any,
    addShape: (shape: any) => void,
    deleteShape: (id: string) => void,
    description?: string
  ) {
    super('Create Shape', description || `Create shape`);
    this.shape = shape;
    this.addShape = addShape;
    this.deleteShape = deleteShape;
  }
  
  execute(): void {
    this.addShape(this.shape);
  }
  
  undo(): void {
    this.deleteShape(this.shape.id);
  }
}

// Base shape creation tool
export abstract class ShapeCreationTool extends BaseTool {
  protected isActive = false;
  protected pendingShapeType: string = 'default';
  
  getBaseManifest(): Partial<IToolManifest> {
    return {
      name: 'Add Shape',
      icon: '➕',
      category: 'create',
      description: 'Add new shapes',
      version: '1.0.0',
      
      requiresSelection: false,
      
      ui: {
        toolbar: true,
        contextMenu: false,
        propertyPanel: false,
        floatingPanel: true
      },
      
      capabilities: {
        modifiesShapes: false,
        createsShapes: true,
        deletesShapes: false,
        modifiesConnectors: false,
        createsConnectors: false,
        deletesConnectors: false,
        requiresUserInput: true,
        supportsMultiSelect: false,
        supportsBatchOperations: false
      },
      
      shortcuts: [{
        key: 'n',
        modifiers: ['shift'],
        description: 'Add new shape'
      }]
    };
  }
  
  onActivate(_context: IToolContext): void {
    super.onActivate(_context);
    this.isActive = true;
    // Change cursor to crosshair when active
    if (_context.stage) {
      _context.stage.container().style.cursor = 'crosshair';
    }
  }
  
  onDeactivate(): void {
    this.isActive = false;
    // Reset cursor
    if (this.context?.stage) {
      this.context.stage.container().style.cursor = 'default';
    }
  }
  
  canExecute(_context: IToolContext): boolean {
    return true;
  }
  
  // Abstract methods
  abstract getShapeTypes(): { value: string; label: string; icon?: string }[];
  abstract createShape(type: string, x: number, y: number): any;
  abstract addShape(shape: any): void;
  abstract deleteShape(id: string): void;
  abstract getDefaultPosition(): { x: number; y: number };
  
  execute(params: { type?: string; x?: number; y?: number }): ICommand | null {
    const type = params.type || this.pendingShapeType;
    const position = params.x && params.y 
      ? { x: params.x, y: params.y }
      : this.getDefaultPosition();
    
    const shape = this.createShape(type, position.x, position.y);
    
    return new CreateShapeCommand(
      shape,
      (s) => this.addShape(s),
      (id) => this.deleteShape(id)
    );
  }
  
  getToolbarComponent(): React.ComponentType<any> {
    return ({ onExecute }: any) => {
      const shapeTypes = this.getShapeTypes();
      
      const handleCreateShape = (type: string) => {
        this.pendingShapeType = type;
        onExecute({ type });
      };
      
      // Map shape type icons to Lucide icons
      const getShapeIcon = (type: string) => {
        switch (type) {
          case 'male': return Square;
          case 'female': return Circle;
          case 'rectangle': return Square;
          case 'circle': return Circle;
          case 'triangle': return Triangle;
          default: return Plus;
        }
      };
      
      return ToolComponentFactory.createSelect({
        icon: Plus,
        placeholder: 'Add Person',
        onValueChange: handleCreateShape,
        options: shapeTypes.map(type => ({
          value: type.value,
          label: type.label,
          icon: getShapeIcon(type.value),
          description: (type as any).description
        }))
      });
    };
  }
}